/**
 * Cart Drawer Enforcer
 * Ensures the native cart drawer takes absolute priority when enabled.
 * Prevents third-party apps and scripts from overriding the native cart behavior.
 */

(function() {
  'use strict';

  // Check global flag first (set in head before this script)
  if (!window.NATIVE_CART_DRAWER_ENABLED) {
    return; // Exit if native drawer is not enabled
  }

  // Add immediate CSS to hide third-party carts ASAP
  const immediateStyle = document.createElement('style');
  immediateStyle.id = 'cart-enforcer-immediate';
  immediateStyle.textContent = `
    /* Immediate hiding of third-party carts */
    cart-drawer:not(cart-drawer-component),
    [id*="CartDrawer"]:not([id*="native"]),
    [class*="side-cart"],
    [class*="ajax-cart"],
    [class*="slide-cart"],
    [data-cart-drawer]:not(cart-drawer-component),
    mini-cart,
    slide-cart,
    ajax-cart-drawer,
    rebuy-cart,
    [data-rebuy-cart],
    .rebuy-cart,
    #rebuy-cart {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
      position: absolute !important;
      left: -9999px !important;
    }
  `;
  document.head.appendChild(immediateStyle);

  const isDrawerEnabled = document.body?.dataset?.cartDrawerEnabled === 'true' || window.NATIVE_CART_DRAWER_ENABLED;
  const isDrawerType = document.body?.dataset?.cartType === 'drawer' || true;
  const shouldEnforceNativeDrawer = isDrawerEnabled && isDrawerType;

  console.log('[Cart Drawer Enforcer] Native cart drawer is enabled and will be enforced');

  /**
   * Get reference to the native cart drawer component
   */
  function getNativeCartDrawer() {
    return document.querySelector('cart-drawer-component');
  }

  /**
   * Intercept and redirect all cart-related clicks to native drawer
   */
  function interceptCartClicks(event) {
    const target = event.target.closest('a[href*="/cart"], button[data-cart], [data-open-cart], [onclick*="cart"], [data-action*="cart"]');
    
    if (!target) return;

    const href = target.getAttribute('href');
    const isNativeTrigger = target.closest('cart-drawer-component');
    
    // If it's not the native trigger, block it
    if (!isNativeTrigger) {
      const nativeDrawer = getNativeCartDrawer();
      
      // Check if it's a cart-related action
      const isCartAction = (href && (href.includes('/cart') || href === '/cart')) ||
                          target.hasAttribute('data-cart') ||
                          target.hasAttribute('data-open-cart') ||
                          (target.getAttribute('onclick') || '').includes('cart');
      
      if (isCartAction && nativeDrawer) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        
        // Open the native drawer
        if (typeof nativeDrawer.open === 'function') {
          nativeDrawer.open();
        } else if (typeof nativeDrawer.showDialog === 'function') {
          nativeDrawer.showDialog();
        }
        
        console.log('[Cart Drawer Enforcer] Intercepted cart click and redirected to native drawer');
        return false;
      }
    }
  }

  /**
   * Prevent third-party drawers from opening by intercepting dialog/modal events
   */
  function preventThirdPartyDialogs(event) {
    const target = event.target;
    
    // Check if it's a third-party cart dialog/modal
    if (target && target.tagName && target.tagName.toLowerCase() === 'dialog') {
      const isNativeDialog = target.closest('cart-drawer-component');
      
      if (!isNativeDialog) {
        const classList = target.className || '';
        const id = target.id || '';
        
        // Check if it's a cart-related dialog
        if (classList.includes('cart') || id.includes('cart') || 
            classList.includes('drawer') || id.includes('drawer')) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          
          // Force close it
          if (target.open && typeof target.close === 'function') {
            target.close();
          }
          
          console.log('[Cart Drawer Enforcer] Blocked third-party cart dialog from opening');
          return false;
        }
      }
    }
  }

  /**
   * Block third-party cart drawer scripts from loading or executing
   */
  function blockThirdPartyCartScripts() {
    // Common third-party cart app selectors
    const thirdPartySelectors = [
      'cart-drawer:not(cart-drawer-component)', // Non-native cart drawers
      '[id*="CartDrawer"]:not([id*="native"])',
      '[class*="side-cart"]',
      '[class*="ajax-cart"]',
      '[class*="slide-cart"]',
      '[data-cart-drawer]:not(cart-drawer-component)',
      'mini-cart',
      'slide-cart',
      'ajax-cart-drawer'
    ];

    // Hide third-party cart elements
    thirdPartySelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(el => {
        el.style.display = 'none';
        el.style.visibility = 'hidden';
        el.style.pointerEvents = 'none';
        el.setAttribute('aria-hidden', 'true');
        console.log('[Cart Drawer Enforcer] Blocked third-party cart element:', selector);
      });
    });
  }

  /**
   * Override common third-party cart methods
   */
  function overrideThirdPartyMethods() {
    // Store reference to native drawer
    const nativeDrawer = getNativeCartDrawer();
    
    // Override common global cart methods
    const methodsToOverride = [
      'openCart',
      'openCartDrawer',
      'showCart',
      'toggleCart',
      'openMiniCart',
      'openSideCart'
    ];

    methodsToOverride.forEach(method => {
      if (window[method]) {
        const original = window[method];
        window[method] = function(...args) {
          console.log(`[Cart Drawer Enforcer] Intercepted ${method}() call, redirecting to native drawer`);
          if (nativeDrawer && typeof nativeDrawer.open === 'function') {
            nativeDrawer.open();
          } else if (nativeDrawer && typeof nativeDrawer.showDialog === 'function') {
            nativeDrawer.showDialog();
          }
          return false;
        };
      }
    });

    // Override Shopify.theme if it exists
    if (window.Shopify && window.Shopify.theme) {
      const originalTheme = window.Shopify.theme;
      window.Shopify.theme = new Proxy(originalTheme, {
        get(target, prop) {
          if (prop === 'openCart' || prop === 'cart' || prop === 'drawer') {
            return function() {
              console.log('[Cart Drawer Enforcer] Intercepted Shopify.theme cart method');
              if (nativeDrawer && typeof nativeDrawer.open === 'function') {
                nativeDrawer.open();
              }
            };
          }
          return target[prop];
        }
      });
    }
  }

  /**
   * Prevent form submissions that might bypass native cart
   */
  function interceptFormSubmissions() {
    document.addEventListener('submit', function(event) {
      const form = event.target;
      
      // Check if it's an add-to-cart form
      if (form.action && form.action.includes('/cart/add')) {
        // Let the form submit normally, but ensure drawer opens after
        const nativeDrawer = getNativeCartDrawer();
        if (nativeDrawer && nativeDrawer.hasAttribute('auto-open')) {
          // Native drawer will handle this via CartAddEvent
          console.log('[Cart Drawer Enforcer] Add to cart form submitted, native drawer will auto-open');
        }
      }
    }, true);
  }

  /**
   * Monitor DOM for dynamically added third-party cart elements
   */
  function monitorDOMChanges() {
    const observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        mutation.addedNodes.forEach(function(node) {
          if (node.nodeType === 1) { // Element node
            // Check if it's a third-party cart element
            const thirdPartyPatterns = [
              /cart.*drawer/i,
              /side.*cart/i,
              /ajax.*cart/i,
              /mini.*cart/i,
              /slide.*cart/i
            ];

            const tagName = node.tagName ? node.tagName.toLowerCase() : '';
            const className = node.className || '';
            const id = node.id || '';

            const isThirdPartyCart = thirdPartyPatterns.some(pattern => 
              pattern.test(tagName) || pattern.test(className) || pattern.test(id)
            );

            // Don't block our native cart-drawer-component
            if (isThirdPartyCart && tagName !== 'cart-drawer-component') {
              console.log('[Cart Drawer Enforcer] Detected third-party cart element, blocking:', node);
              node.style.display = 'none';
              node.style.visibility = 'hidden';
              node.style.pointerEvents = 'none';
              node.setAttribute('aria-hidden', 'true');
            }
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Add CSS to enforce native drawer visibility
   */
  function addEnforcementStyles() {
    const style = document.createElement('style');
    style.id = 'cart-drawer-enforcer-styles';
    style.textContent = `
      /* Hide third-party cart drawers when native is enabled */
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] cart-drawer:not(cart-drawer-component),
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] [id*="CartDrawer"]:not([id*="native"]),
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] [class*="side-cart"],
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] [class*="ajax-cart"],
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] [class*="slide-cart"],
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] mini-cart,
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] slide-cart,
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] ajax-cart-drawer {
        display: none !important;
        visibility: hidden !important;
        pointer-events: none !important;
        opacity: 0 !important;
      }

      /* Ensure native cart drawer is always visible */
      body[data-cart-drawer-enabled="true"][data-cart-type="drawer"] cart-drawer-component {
        display: block !important;
        visibility: visible !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Initialize enforcement
   */
  function init() {
    console.log('[Cart Drawer Enforcer] Initializing native cart drawer enforcement');
    
    // Add enforcement styles immediately
    addEnforcementStyles();
    
    // Block third-party elements
    blockThirdPartyCartScripts();
    
    // Override methods
    overrideThirdPartyMethods();
    
    // Intercept clicks (use capture phase to get events first)
    document.addEventListener('click', interceptCartClicks, true);
    
    // Intercept dialog/modal opening
    document.addEventListener('toggle', preventThirdPartyDialogs, true);
    
    // Intercept form submissions
    interceptFormSubmissions();
    
    // Monitor for dynamic changes
    monitorDOMChanges();
    
    // Close any third-party cart dialogs that might already be open
    setTimeout(() => {
      const thirdPartyDialogs = document.querySelectorAll('dialog:not(cart-drawer-component dialog)');
      thirdPartyDialogs.forEach(dialog => {
        const classList = dialog.className || '';
        const id = dialog.id || '';
        if ((classList.includes('cart') || id.includes('cart')) && dialog.open) {
          dialog.close();
          console.log('[Cart Drawer Enforcer] Closed pre-existing third-party cart dialog');
        }
      });
    }, 100);
    
    console.log('[Cart Drawer Enforcer] Native cart drawer enforcement active');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run on page load to catch any late-loading scripts
  window.addEventListener('load', function() {
    blockThirdPartyCartScripts();
  });

  // Aggressive continuous monitoring - check every 100ms for third-party cart dialogs
  setInterval(function() {
    const thirdPartyDialogs = document.querySelectorAll('dialog:not(cart-drawer-component dialog)');
    thirdPartyDialogs.forEach(dialog => {
      const classList = dialog.className || '';
      const id = dialog.id || '';
      
      // Check if it's a cart-related dialog and it's open
      if ((classList.includes('cart') || id.includes('cart') || 
           classList.includes('drawer') || id.includes('drawer')) && dialog.open) {
        dialog.close();
        dialog.style.display = 'none';
        dialog.style.visibility = 'hidden';
        console.log('[Cart Drawer Enforcer] Aggressively closed third-party cart dialog:', id || classList);
      }
    });
    
    // Also check for non-dialog third-party carts that might be visible
    const thirdPartyCarts = document.querySelectorAll(`
      cart-drawer:not(cart-drawer-component),
      [id*="CartDrawer"]:not([id*="native"]),
      [class*="side-cart"],
      [class*="ajax-cart"],
      rebuy-cart,
      [data-rebuy-cart]
    `);
    
    thirdPartyCarts.forEach(cart => {
      if (cart.style.display !== 'none') {
        cart.style.display = 'none';
        cart.style.visibility = 'hidden';
        cart.style.opacity = '0';
        cart.style.pointerEvents = 'none';
        console.log('[Cart Drawer Enforcer] Hid visible third-party cart element');
      }
    });
  }, 100);

})();
